/**
 * Post-payment grant poll, shared by every web checkout path.
 *
 * Paddle's `checkout.completed` fires the instant payment is confirmed in the
 * browser, but the grant (Kit activation, token credit, entitlement) lands
 * asynchronously via the webhook a beat later. Polling the API until the grant
 * shows up flips the UI in-moment instead of waiting on the WS `user/updated`
 * event or a manual refresh. Short, front-loaded delays catch the common fast
 * case quickly; ~10s total covers a slow webhook.
 *
 * This only ACCELERATES the flip — the WS event and the reconcile layers
 * (L1/L2/L3) remain the authoritative backstop if the grant takes longer than
 * this window or the poll errors out.
 */
const GRANT_POLL_DELAYS_MS = [700, 1000, 1500, 2500, 4000];

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Run `hasLanded` on the backoff schedule until it returns true (→ resolves
 * true) or the schedule runs out (→ resolves false). A throwing check counts as
 * "not yet" and keeps polling: a transient read failure must never surface to
 * the buyer, and the reconcile backstop covers a total miss.
 */
export async function pollUntilGranted(
  hasLanded: () => Promise<boolean>
): Promise<boolean> {
  for (const delay of GRANT_POLL_DELAYS_MS) {
    await sleep(delay);

    try {
      if (await hasLanded()) {
        return true;
      }
    } catch {
      continue;
    }
  }

  return false;
}
